import { useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';
import type { LoadState } from '../loadState';

export interface SessionCoverageRow {
  id: string;
  date: string;
  title: string | null;
  duration_mins: number | null;
  phase_coverage: {
    attacking?: number;
    defending?: number;
    transitions?: number;
    individual?: number;
  } | null;
}

interface SessionCoverageResult {
  rows: SessionCoverageRow[];
  state: LoadState;
  errorMessage?: string;
}

/**
 * Reads the team's recent sessions with their phase_coverage jsonb for
 * the curriculum planner heat-map. Defaults to a 90-day lookback so the
 * grid spans roughly a season cycle without dragging in archive data.
 */
export function useTeamSessionCoverage(
  teamId: string | null | undefined,
  teamLoading: boolean,
  lookbackDays = 90,
): SessionCoverageResult {
  const [rows, setRows] = useState<SessionCoverageRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    if (teamLoading) {
      setState('loading');
      return;
    }
    if (!teamId) {
      setRows([]);
      setState('empty');
      return;
    }
    if (isPlaceholderSupabase()) {
      setRows([]);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      const since = new Date();
      since.setDate(since.getDate() - lookbackDays);
      const sinceIso = since.toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('sessions')
        .select('id, date, title, duration_mins, phase_coverage')
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .gte('date', sinceIso)
        .order('date', { ascending: true });
      if (cancelled) return;
      if (error) {
        setErrorMessage(error.message);
        setState('error');
        return;
      }
      const list = (data ?? []) as unknown as SessionCoverageRow[];
      setRows(list);
      setState(list.length > 0 ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, teamLoading, lookbackDays]);

  return { rows, state, errorMessage };
}
